import { useEffect, useState } from "react";
import { Gear, Buildings } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError } from "../lib/utils-extra";

const FIELDS = [
  ["company_name", "Company Name"],
  ["business_address", "Business Address"],
  ["contact_number", "Contact Number"],
  ["email", "Email Address"],
  ["website", "Website"],
  ["logo_url", "Logo URL"],
  ["tax_id", "Tax ID (optional)"],
];
const TEXTAREAS = [
  ["bank_details", "Bank Details"],
  ["payment_instructions", "Payment Instructions"],
  ["invoice_footer", "Invoice Footer Text"],
];

function BrandingForm({ data, onChange, prefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {FIELDS.map(([k, label]) => (
        <div key={k} className={k === "business_address" ? "sm:col-span-2" : ""}>
          <Label className="small-label">{label}</Label>
          <Input className="mt-1.5" value={data[k] || ""} onChange={(e) => onChange(k, e.target.value)} data-testid={`${prefix}-${k}`} />
        </div>
      ))}
      {TEXTAREAS.map(([k, label]) => (
        <div key={k} className="sm:col-span-2">
          <Label className="small-label">{label}</Label>
          <Textarea rows={2} className="mt-1.5" value={data[k] || ""} onChange={(e) => onChange(k, e.target.value)} data-testid={`${prefix}-${k}`} />
        </div>
      ))}
    </div>
  );
}

export default function PlatformSettings() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/platform/settings").then(({ data }) => setSettings(data)); }, []);
  if (!settings) return <div className="text-sm text-[#475569]">Loading…</div>;

  const setBrand = (source, k, v) => setSettings({ ...settings, [source]: { ...settings[source], [k]: v } });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/platform/settings", {
        default_branding_source: settings.default_branding_source,
        rmbc: settings.rmbc,
        remediscore: settings.remediscore,
      });
      toast.success("Platform settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="platform-settings-page">
      <div>
        <div className="small-label">Platform</div>
        <h1 className="font-display text-3xl font-semibold mt-1">Platform Settings</h1>
        <p className="text-[#475569] text-sm mt-1">
          Configurable invoice branding sources. Choose a default and edit each brand profile.
          Per-tenant branding is set inside each Tenant Detail workspace. Nothing is hardcoded.
        </p>
      </div>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-5">
          <Label className="small-label">Default Branding Source</Label>
          <Select value={settings.default_branding_source} onValueChange={(v) => setSettings({ ...settings, default_branding_source: v })}>
            <SelectTrigger className="mt-1.5 sm:w-72" data-testid="default-branding-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="rmbc">RMBC</SelectItem>
              <SelectItem value="remediscore">RemediScore</SelectItem>
              <SelectItem value="per_tenant">Per Tenant</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4"><Buildings size={20} weight="duotone" color="#0EA5E9" /><h2 className="font-display text-lg font-semibold">RMBC Branding</h2></div>
          <BrandingForm data={settings.rmbc} prefix="rmbc" onChange={(k, v) => setBrand("rmbc", k, v)} />
        </CardContent>
      </Card>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4"><Gear size={20} weight="duotone" color="#0284C7" /><h2 className="font-display text-lg font-semibold">RemediScore Branding</h2></div>
          <BrandingForm data={settings.remediscore} prefix="remediscore" onChange={(k, v) => setBrand("remediscore", k, v)} />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="platform-settings-save" className="bg-[#0EA5E9] hover:bg-[#0284C7]">{busy ? "Saving…" : "Save platform settings"}</Button>
      </div>
    </div>
  );
}
